import React, { useMemo } from "react";
import cx from "classnames";
import { ScrollArea } from "@picklebet/ui";

import { Outcome, getOutcomeName } from "./Outcome";
import { ExpandableOutcomes } from "./ExpandableOutcomes";
import { CompetitorAvatar } from "@/features/betting/components/CompetitorAvatar";
import { isRacingCompetitor } from "@/hooks/firestore/betting/useRacingEvents";
import { OverflowText } from "@picklebet/ui";
import {
  NO_PLAYER,
  sortPlayerOutcomes,
  groupByPlayer,
  orderedSlots,
  outcomeSlotKey,
  type PlayerMarketLayoutProps,
} from "./playerMarketLayout";
import { getOutcomeCompetitor, type OutcomeCompetitors } from "./outcomeLayout";
import { useIsLiveViewOnly } from "@/features/betting/hooks/useIsLiveViewOnly";

import type {
  EventStatus,
  ToggleSelectionPayload,
} from "@/hooks/firestore/betting/types";
import type { ProcessedOutcome } from "@/hooks/firestore/betting/useEvent";

import * as styles from "./PlayerMarketRows.module.scss";
import * as outcomeStyles from "./Outcome.module.scss";

// rows shown before the "show more" toggle
const INITIAL_ROWS = 2;

// fills a slot a player has no price for, so every row has the same number of
// columns. uses Outcome's own CSS classes, not the component - no real odds/status here
const PlaceholderCell = ({ label }: { label: string }) => (
  <div className={cx(outcomeStyles.container, outcomeStyles.card)}>
    <div
      className={cx(
        outcomeStyles.outcome,
        outcomeStyles.unclickable,
        styles.outcome,
      )}
    >
      <div className={outcomeStyles.detail}>
        <div className={outcomeStyles.name}>{label}</div>
        <div className={outcomeStyles.odds}>-</div>
      </div>
    </div>
  </div>
);

type PlayerRowProps = {
  player: OutcomeCompetitors[string] | null;
  // the player's franchise, when the main market can resolve one
  team: OutcomeCompetitors[string] | null;
  outcomes: (ProcessedOutcome | null)[];
  // labels a placeholder when the outcome at that index is null
  slots: ProcessedOutcome[];
  eventId: string;
  eventStatus?: EventStatus;
  oddsFormat?: string;
  selections: string[];
  toggleSelection: (pick: ToggleSelectionPayload) => void;
  isLoading?: boolean;
  renderFieldsKey?: string;
  isSuspended?: boolean;
};

const PlayerRow = ({
  player,
  team,
  outcomes,
  slots,
  eventId,
  eventStatus,
  oddsFormat,
  selections,
  toggleSelection,
  isLoading,
  renderFieldsKey,
  isSuspended,
}: PlayerRowProps) => {
  // the no-player row has no name to show, so borrow the market's
  const label = player?.name ?? outcomes.find(Boolean)?.market?.name;
  // team logo for now, falling back to the player when the franchise can't be
  // resolved
  const competitor = team ?? player;
  const avatar =
    competitor && !isRacingCompetitor(competitor) ? competitor : undefined;

  return (
    <div className={styles.playerRow}>
      <div className={styles.player}>
        <CompetitorAvatar
          competitor={avatar}
          className={styles.avatar}
          reserveAvatarSpace
        />
        <div className={styles.playerMeta}>
          <OverflowText>
            <span className={styles.playerName}>{label}</span>
          </OverflowText>
        </div>
      </div>

      <ScrollArea.Root
        className={cx(styles.scroll, { [styles.isSuspended]: isSuspended })}
        orientation="horizontal"
      >
        <ScrollArea.Viewport dragToScroll>
          <div className={styles.outcomes}>
            {outcomes.map((outcome, index) =>
              outcome ? (
                <Outcome
                  key={outcome.id}
                  id={outcome.id}
                  variant="card"
                  eventId={eventId}
                  eventStatus={eventStatus}
                  marketId={outcome.market?.id}
                  outcome={outcome}
                  hideCompetitor
                  className={styles.outcome}
                  toggleSelection={toggleSelection}
                  isSuspended={!outcome.active}
                  isSelected={selections.includes(outcome.id)}
                  oddsFormat={oddsFormat}
                  status={outcome.market?.status}
                  isLoading={isLoading}
                  renderFieldsKey={renderFieldsKey}
                />
              ) : (
                <PlaceholderCell
                  key={`placeholder-${index}`}
                  label={getOutcomeName(
                    slots[index],
                    undefined,
                    renderFieldsKey,
                  )}
                />
              ),
            )}
          </div>
        </ScrollArea.Viewport>
        <ScrollArea.Shadows />
      </ScrollArea.Root>
    </div>
  );
};

// one row per player, outcomes scrolling horizontally.
const PlayerMarketRows = ({
  outcomes,
  competitors,
  eventId,
  eventStatus,
  oddsFormat,
  selections,
  toggleSelection,
  isLoading = false,
  mainMarket,
  renderFieldsKey,
  isSuspended = false,
  forceExpand,
}: PlayerMarketLayoutProps) => {
  const isLiveViewOnly = useIsLiveViewOnly(eventStatus);

  const sortedOutcomes = useMemo(
    () => sortPlayerOutcomes(outcomes, renderFieldsKey, isLiveViewOnly),
    [outcomes, renderFieldsKey, isLiveViewOnly],
  );

  // every slot any player has, in order - every row pads out to this same
  // set so columns line up. kept outside `rows` so placeholders can read
  // their slot's title
  const slots = useMemo(
    () => orderedSlots(sortedOutcomes, renderFieldsKey),
    [sortedOutcomes, renderFieldsKey],
  );

  const rows = useMemo(() => {
    // one row per player, ordered by highest threshold reached first, then
    // smallest odds at that threshold
    const grouped = groupByPlayer(sortedOutcomes);
    const slotKeys = slots.map((outcome) =>
      outcomeSlotKey(outcome, renderFieldsKey),
    );

    // sortedOutcomes is already in ascending threshold order, so a player's
    // last outcome is their highest
    const highestSlotIndex = (playerOutcomes: ProcessedOutcome[]) => {
      const last = playerOutcomes[playerOutcomes.length - 1];
      return last
        ? slotKeys.indexOf(outcomeSlotKey(last, renderFieldsKey))
        : -1;
    };

    const ordered = [...grouped].sort(([, aOutcomes], [, bOutcomes]) => {
      const aIndex = highestSlotIndex(aOutcomes);
      const bIndex = highestSlotIndex(bOutcomes);
      // priceless rows sink, rather than sorting differently every render
      if (aIndex === -1) return bIndex === -1 ? 0 : 1;
      if (bIndex === -1) return -1;
      // deeper threshold sorts first
      if (aIndex !== bIndex) return bIndex - aIndex;

      const a = aOutcomes[aOutcomes.length - 1]?.odds;
      const b = bOutcomes[bOutcomes.length - 1]?.odds;
      if (a == null) return b == null ? 0 : 1;
      if (b == null) return -1;
      return a - b;
    });

    return ordered.map(([playerId, playerOutcomes]) => {
      // a player can have more than one outcome landing on the same
      // column (e.g. no render field to distinguish them) - last one wins,
      // so each column has at most one cell per player
      const bySlot = new Map(
        playerOutcomes.map((outcome) => [
          outcomeSlotKey(outcome, renderFieldsKey),
          outcome,
        ]),
      );
      return [
        playerId,
        slotKeys.map((key) => bySlot.get(key) ?? null),
      ] as const;
    });
  }, [sortedOutcomes, slots, renderFieldsKey]);

  const renderRow = ([playerId, playerOutcomes]: (typeof rows)[number]) => (
    <PlayerRow
      key={playerId}
      player={playerId === NO_PLAYER ? null : competitors[playerId] ?? null}
      team={getOutcomeCompetitor(
        competitors,
        playerOutcomes.find(Boolean) ?? undefined,
        mainMarket,
      )}
      outcomes={playerOutcomes}
      slots={slots}
      eventId={eventId}
      eventStatus={eventStatus}
      oddsFormat={oddsFormat}
      selections={selections}
      toggleSelection={toggleSelection}
      isLoading={isLoading}
      renderFieldsKey={renderFieldsKey}
      isSuspended={isSuspended}
    />
  );

  return (
    <ExpandableOutcomes
      className={styles.players}
      collapsedClassName={styles.drawer}
      hiddenCount={Math.max(rows.length - INITIAL_ROWS, 0)}
      collapsed={rows.slice(INITIAL_ROWS).map(renderRow)}
      forceExpand={forceExpand}
    >
      {rows.slice(0, INITIAL_ROWS).map(renderRow)}
    </ExpandableOutcomes>
  );
};

export { PlayerMarketRows };
